if (!customElements.get('mewmora-questionnaire')) {
  customElements.define(
    'mewmora-questionnaire',
    class MewmoraQuestionnaire extends HTMLElement {
      connectedCallback() {
        if (this.initialized) return;

        this.stepsWrap = this.querySelector('[data-questionnaire-steps]');
        this.steps = Array.from(this.querySelectorAll('[data-questionnaire-step]'));
        this.readyPanel = this.querySelector('[data-questionnaire-ready]');
        this.currentStepEl = this.querySelector('[data-current-step]');
        this.progressFill = this.querySelector('[data-progress-fill]');
        this.backButton = this.querySelector('[data-questionnaire-back]');
        this.skipButton = this.querySelector('[data-questionnaire-skip]');
        this.nextButton = this.querySelector('[data-questionnaire-next]');
        this.editButton = this.querySelector('[data-questionnaire-edit]');
        this.birthdayError = this.querySelector('[data-birthday-error]');
        this.notesCount = this.querySelector('[data-notes-count]');
        this.form = this.closest('form');
        this.nativeActions = this.form ? this.form.querySelector('[data-mewmora-native-actions]') : null;
        this.purchaseReady = false;

        if (!this.stepsWrap || this.steps.length !== 5 || !this.nextButton) return;

        this.state = this.loadState();
        this.pruneRadioAnswers();
        this.reflectStateToControls();
        this.bindControls();
        this.syncLineItemProperties();
        this.render(false);
        this.classList.add('is-initialized');

        // Progressive enhancement: hide Shopify's native actions only after the
        // questionnaire is fully initialized. If JavaScript fails, checkout
        // remains available instead of leaving the shopper trapped.
        if (this.nativeActions) {
          this.nativeActions.hidden = true;
          this.nativeActions.setAttribute('aria-hidden', 'true');
        }

        // Phase contract for the next step. A real plan integration must emit
        // this event (or call setPurchaseReady(true)) only after a valid one-time
        // or Shopify selling-plan choice exists. Step 3a never emits it.
        this.addEventListener('mewmora:purchase-ready', (event) => {
          this.setPurchaseReady(event.detail?.ready === true);
        });

        this.initialized = true;
      }

      defaultState() {
        return {
          version: 1,
          currentIndex: 0,
          complete: false,
          answers: {
            catName: '',
            birthdayMonth: '',
            birthdayYear: '',
            catnipPreference: '',
            playPreference: '',
            notes: '',
          },
        };
      }

      normalizeState(candidate) {
        const clean = this.defaultState();
        if (!candidate || typeof candidate !== 'object') return clean;

        const index = Number(candidate.currentIndex);
        if (Number.isFinite(index)) clean.currentIndex = Math.min(Math.max(Math.round(index), 0), 4);
        clean.complete = candidate.complete === true;

        const source = candidate.answers && typeof candidate.answers === 'object' ? candidate.answers : {};
        clean.answers.catName = this.cleanText(source.catName, 40);
        clean.answers.birthdayMonth = this.validMonth(source.birthdayMonth);
        clean.answers.birthdayYear = this.validYear(source.birthdayYear);
        clean.answers.catnipPreference = this.cleanText(source.catnipPreference, 80);
        clean.answers.playPreference = this.cleanText(source.playPreference, 100);
        clean.answers.notes = this.cleanText(source.notes, 250, true);
        return clean;
      }

      cleanText(value, maxLength, keepLines = false) {
        let clean = value == null ? '' : String(value);
        clean = clean.replace(/[<>]/g, '');
        if (!keepLines) clean = clean.replace(/\s+/g, ' ');
        return clean.slice(0, maxLength);
      }

      validMonth(value) {
        const month = Number(value);
        return Number.isInteger(month) && month >= 1 && month <= 12 ? String(month) : '';
      }

      validYear(value) {
        const year = Number(value);
        const currentYear = new Date().getFullYear();
        return Number.isInteger(year) && year >= currentYear - 24 && year <= currentYear ? String(year) : '';
      }

      loadState() {
        // Answers intentionally start fresh on each product-page load. This
        // prevents a later shopper in the same browser from inheriting another
        // cat's order properties. Back/Next still preserve values in memory.
        return this.defaultState();
      }

      saveState() {
        // Deliberately in-memory only; see loadState().
      }

      pruneRadioAnswers() {
        ['catnipPreference', 'playPreference'].forEach((key) => {
          const allowed = Array.from(this.querySelectorAll(`[data-answer-radio="${key}"]`)).map(
            (input) => input.value
          );
          if (!allowed.includes(this.state.answers[key])) this.state.answers[key] = '';
        });
      }

      bindControls() {
        Array.from(this.querySelectorAll('[data-answer-field]')).forEach((control) => {
          const eventName = control.tagName === 'SELECT' ? 'change' : 'input';
          control.addEventListener(eventName, () => this.handleFieldInput(control));
        });

        Array.from(this.querySelectorAll('[data-answer-radio]')).forEach((control) => {
          control.addEventListener('change', () => {
            if (!control.checked) return;
            const key = control.dataset.answerRadio;
            if (!Object.prototype.hasOwnProperty.call(this.state.answers, key)) return;
            this.state.answers[key] = this.cleanText(control.value, key === 'playPreference' ? 100 : 80);
            this.commitInputChange();
          });
        });

        this.backButton?.addEventListener('click', () => this.goBack());
        this.skipButton?.addEventListener('click', () => this.skipCurrentStep());
        this.nextButton?.addEventListener('click', () => this.goForward());
        this.editButton?.addEventListener('click', () => this.editAnswers());

        // Capture before Dawn's product-form handler. Until a real plan stage
        // explicitly unlocks purchase, Enter in a text field cannot bypass the
        // questionnaire or submit a one-time order accidentally.
        this.form?.addEventListener('submit', (event) => this.handleFormSubmit(event), true);
      }

      handleFormSubmit(event) {
        this.syncLineItemProperties();
        if (this.purchaseReady) return;
        event.preventDefault();
        event.stopImmediatePropagation();
      }

      setPurchaseReady(isReady) {
        this.purchaseReady = isReady === true && this.state.complete === true;
        if (!this.nativeActions) return;
        this.nativeActions.hidden = !this.purchaseReady;
        this.nativeActions.setAttribute('aria-hidden', String(!this.purchaseReady));
      }

      handleFieldInput(control) {
        const key = control.dataset.answerField;
        if (!Object.prototype.hasOwnProperty.call(this.state.answers, key)) return;

        if (key === 'catName') {
          const clean = this.cleanText(control.value, 40);
          if (control.value !== clean) control.value = clean;
          this.state.answers.catName = clean.trim();
        } else if (key === 'notes') {
          const clean = this.cleanText(control.value, 250, true);
          if (control.value !== clean) control.value = clean;
          this.state.answers.notes = clean.trim();
        } else if (key === 'birthdayMonth') {
          this.state.answers.birthdayMonth = this.validMonth(control.value);
        } else if (key === 'birthdayYear') {
          this.state.answers.birthdayYear = this.validYear(control.value);
        }

        this.commitInputChange();
      }

      commitInputChange() {
        this.state.complete = false;
        this.setPurchaseReady(false);
        this.saveState();
        this.syncLineItemProperties();
        this.updateNotesCounter();
        this.updateNavigationState();
      }

      reflectStateToControls() {
        const answers = this.state.answers;
        Array.from(this.querySelectorAll('[data-answer-field]')).forEach((control) => {
          const key = control.dataset.answerField;
          control.value = answers[key] || '';
        });

        Array.from(this.querySelectorAll('[data-answer-radio]')).forEach((control) => {
          const key = control.dataset.answerRadio;
          control.checked = (answers[key] || '') === control.value;
        });

        this.updateNotesCounter();
      }

      updateNotesCounter() {
        if (this.notesCount) this.notesCount.textContent = String((this.state.answers.notes || '').length);
      }

      currentStepType() {
        return this.steps[this.state.currentIndex]?.dataset.stepType || '';
      }

      currentStepHasAnswer() {
        const answers = this.state.answers;
        switch (this.currentStepType()) {
          case 'cat-name':
            return answers.catName.trim() !== '';
          case 'birthday':
            return answers.birthdayMonth !== '' && answers.birthdayYear !== '';
          case 'catnip':
            return answers.catnipPreference !== '';
          case 'play-preference':
            return answers.playPreference !== '';
          case 'notes':
            return answers.notes.trim() !== '';
          default:
            return false;
        }
      }

      birthdayIsPartial() {
        const hasMonth = this.state.answers.birthdayMonth !== '';
        const hasYear = this.state.answers.birthdayYear !== '';
        return hasMonth !== hasYear;
      }

      birthdayIsFuture() {
        const month = Number(this.state.answers.birthdayMonth);
        const year = Number(this.state.answers.birthdayYear);
        if (!month || !year) return false;
        const now = new Date();
        return year > now.getFullYear() || (year === now.getFullYear() && month > now.getMonth() + 1);
      }

      birthdayIsInvalid() {
        return this.birthdayIsPartial() || this.birthdayIsFuture();
      }

      updateNavigationState() {
        const canContinue = this.currentStepHasAnswer() && !this.birthdayIsInvalid();
        this.nextButton.disabled = !canContinue;
        this.nextButton.setAttribute('aria-disabled', String(!canContinue));

        if (this.birthdayError) {
          const showError = this.currentStepType() === 'birthday' && this.birthdayIsInvalid();
          this.birthdayError.hidden = !showError;
          const birthdayControls = this.querySelectorAll(
            '[data-answer-field="birthdayMonth"], [data-answer-field="birthdayYear"]'
          );
          birthdayControls.forEach((control) => {
            if (showError) control.setAttribute('aria-invalid', 'true');
            else control.removeAttribute('aria-invalid');
          });
        }
      }

      goForward() {
        if (!this.currentStepHasAnswer() || this.birthdayIsInvalid()) {
          this.updateNavigationState();
          return;
        }

        if (this.state.currentIndex >= this.steps.length - 1) {
          this.completeQuestionnaire();
          return;
        }

        this.state.currentIndex += 1;
        this.state.complete = false;
        this.saveState();
        this.render(true);
      }

      goBack() {
        if (this.state.currentIndex <= 0) return;
        this.state.currentIndex -= 1;
        this.state.complete = false;
        this.saveState();
        this.render(true);
      }

      skipCurrentStep() {
        const type = this.currentStepType();
        const answers = this.state.answers;

        if (type === 'cat-name') answers.catName = '';
        if (type === 'birthday') {
          answers.birthdayMonth = '';
          answers.birthdayYear = '';
        }
        if (type === 'catnip') answers.catnipPreference = '';
        if (type === 'play-preference') answers.playPreference = '';
        if (type === 'notes') answers.notes = '';

        this.reflectStateToControls();
        this.syncLineItemProperties();

        if (this.state.currentIndex >= this.steps.length - 1) {
          this.completeQuestionnaire();
          return;
        }

        this.state.currentIndex += 1;
        this.state.complete = false;
        this.saveState();
        this.render(true);
      }

      completeQuestionnaire() {
        this.setPurchaseReady(false);
        this.state.complete = true;
        this.state.currentIndex = this.steps.length - 1;
        this.saveState();
        this.syncLineItemProperties();
        this.render(true);

        this.dispatchEvent(
          new CustomEvent('mewmora:questionnaire-complete', {
            bubbles: true,
            detail: { answers: { ...this.state.answers } },
          })
        );
      }

      editAnswers() {
        this.setPurchaseReady(false);
        this.state.complete = false;
        this.state.currentIndex = 0;
        this.saveState();
        this.render(true);
      }

      render(shouldFocus) {
        if (this.state.complete) {
          this.stepsWrap.hidden = true;
          this.readyPanel.hidden = false;
          if (shouldFocus) this.readyPanel.focus({ preventScroll: true });
          return;
        }

        this.stepsWrap.hidden = false;
        this.readyPanel.hidden = true;

        this.steps.forEach((step, index) => {
          step.hidden = index !== this.state.currentIndex;
        });

        if (this.currentStepEl) this.currentStepEl.textContent = String(this.state.currentIndex + 1);
        if (this.progressFill) {
          this.progressFill.style.width = `${((this.state.currentIndex + 1) / this.steps.length) * 100}%`;
        }

        if (this.backButton) this.backButton.hidden = this.state.currentIndex === 0;

        const nextLabel = this.nextButton.dataset.nextLabel || 'Next';
        const finishLabel = this.nextButton.dataset.finishLabel || 'Save & continue';
        this.nextButton.textContent = this.state.currentIndex === this.steps.length - 1 ? finishLabel : nextLabel;

        this.updateNavigationState();

        if (shouldFocus) {
          const question = this.steps[this.state.currentIndex]?.querySelector('.mewmora-questionnaire__question');
          question?.setAttribute('tabindex', '-1');
          question?.focus({ preventScroll: true });
        }
      }

      syncLineItemProperties() {
        const monthNames = [
          'January',
          'February',
          'March',
          'April',
          'May',
          'June',
          'July',
          'August',
          'September',
          'October',
          'November',
          'December',
        ];
        const answers = this.state.answers;
        const monthNumber = Number(answers.birthdayMonth);
        const birthday =
          monthNumber >= 1 && monthNumber <= 12 && answers.birthdayYear
            ? `${monthNames[monthNumber - 1]} ${answers.birthdayYear}`
            : '';

        const values = {
          catName: answers.catName.trim(),
          birthday,
          catnipPreference: answers.catnipPreference,
          playPreference: answers.playPreference,
          notes: answers.notes.trim(),
        };

        Array.from(this.querySelectorAll('[data-questionnaire-property]')).forEach((input) => {
          const key = input.dataset.questionnaireProperty;
          const value = values[key] || '';
          input.value = value;
          input.disabled = value === '';
        });
      }
    }
  );
}
